/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React from 'react'
import RegularTemplate from '@/components/templates/RegularTemplate'
import FilterAndSortBarWithActions from '@/components/listings/FilterAndSortBarWithActions'
import { useFetchItemsWithParamsV2 } from '@/hooks/fetch/useFetchItemsWithParamsV2'
import {
  LICENSED_ASSET_TITLE,
  BUILD_AI_METADATA_BATCH_LABEL,
  EXPORT_LICENSED_ASSETS_LABEL
} from '@/configs/strings/licensed-assets'
import { filterOptions, sortOptions } from './handlers'
import { Link } from 'react-router-dom'
import { aiMetadataBatchesUrls } from '@/configs/urls'
import { Box, Button } from '@chakra-ui/react'
import { getLicensedAssets, getLicensedAssetsAsCsv } from '@/services/licensed-assets'
import LicensedAssetCard from '@/components/cards/LicensedAssetCard'
import useLicensedAssets from './useLicensedAssets'
import { generalBatchItemStates } from '@/configs/server-enums'
import ItemsListWithToggleLayout from '@/components/wrappers/general/ItemsListWithToggleLayout'
import DownloadFileButton from '@/components/buttons/DownloadFileButton'
import DefaultTableHidingColumns from '@/components/tables/DefaultTableHidingColumns'

const LicensedAssetsList = () => {
  const {
    itemsResponse,
    loading,
    onChangePage,
    page,
    searchParams,
    setSearchParams,
    totalPages
  } = useFetchItemsWithParamsV2({
    fetchService: getLicensedAssets,
    fetchParams: { samsState: generalBatchItemStates.created }
  })
  const {
    getCardProps,
    getItemsPropsForTable,
    tableColumns,
    tableInitialState
  } = useLicensedAssets()

  const CreateButton = () => (
    <Button
      as={Link}
      to={`/${aiMetadataBatchesUrls.base}/${aiMetadataBatchesUrls.create}`}>
      { BUILD_AI_METADATA_BATCH_LABEL }
    </Button>
  )

  const licensedAssetCards = itemsResponse?.assets.map(item => {
    const cardProps = getCardProps(item)

    return (
      <LicensedAssetCard
        key={cardProps.key}
        {...cardProps}
      />
    )
  })

  const tableLicensedAssets = <DefaultTableHidingColumns
    columns={tableColumns}
    getItemProps={getItemsPropsForTable}
    data={itemsResponse?.assets}
    tableInitialState={tableInitialState}
    visibleColumnsParamKey="visibleColumns"
  />

  return (
    <RegularTemplate
      title={LICENSED_ASSET_TITLE}
      loadingContent={loading && !itemsResponse}
      childrenHeader={CreateButton}
    >
      <Box
        display="flex"
        justifyContent="flex-end"
        className="mb-5 mt-5"
      >
        <DownloadFileButton
          service={(getAccessToken) => getLicensedAssetsAsCsv(
            { aiMetadataState: generalBatchItemStates.created },
            getAccessToken
          )}
          isDisabled={!itemsResponse?.assets?.length}
          dataKey="csv"
        >
          { EXPORT_LICENSED_ASSETS_LABEL }
        </DownloadFileButton>
      </Box>
      { itemsResponse &&
        <ItemsListWithToggleLayout
          cards={licensedAssetCards}
          currentPage={page}
          header={
            <FilterAndSortBarWithActions
              currentValues={searchParams}
              filterOptions={filterOptions}
              onFilterResults={(filter) => setSearchParams(filter)}
              onReset={() => setSearchParams({})}
              sortOptions={sortOptions}
            />
          }
          itemsLayout={searchParams.itemsLayout}
          isLoading={loading}
          onChangePage={onChangePage}
          searchParams={searchParams}
          table={tableLicensedAssets}
          totalItems={itemsResponse?.total}
          totalPages={totalPages}
        />
      }
    </RegularTemplate>
  )
}

export default LicensedAssetsList
